import React, { useState, type ReactNode } from 'react';

import { Button, type ButtonProps } from '../Button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent as AlertDialogContentPrimitive,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from './AlertDialog';

type BaseAlertDialogProps = {
  title: string;
  description?: string;
  children?: ReactNode;
  cancelText?: string;
  confirmText?: string;
  onConfirm?: () => void;
  variant?: 'default' | 'destructive';
};

type ControlledDialogProps = BaseAlertDialogProps & {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
  dialogDescriptionClassName?: string;
};

type AlertDialogWithTriggerProps = BaseAlertDialogProps & {
  trigger: ReactNode;
  isLoading?: boolean;
  buttonProps?: ButtonProps;
  dialogDescriptionClassName?: string;
};

export const ControlledAlertDialog = ({
  isOpen,
  onClose,
  title,
  description,
  dialogDescriptionClassName,
  children,
  cancelText = 'Cancel',
  confirmText = 'Continue',
  onConfirm,
  variant = 'default',
  className,
}: ControlledDialogProps) => {
  const handleConfirm = () => {
    onConfirm?.();
    onClose();
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContentPrimitive className={className}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description && (
            <AlertDialogDescription className={dialogDescriptionClassName}>{description}</AlertDialogDescription>
          )}
        </AlertDialogHeader>
        {children}
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onClose}>{cancelText}</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            className={
              variant === 'destructive' ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''
            }
          >
            {confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContentPrimitive>
    </AlertDialog>
  );
};

export const AlertDialogWithTrigger = ({
  title,
  description,
  children,
  trigger,
  cancelText = 'Cancel',
  confirmText = 'Continue',
  onConfirm,
  variant = 'default',
  isLoading = false,
  buttonProps,
  dialogDescriptionClassName,
}: AlertDialogWithTriggerProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const renderTrigger = () => {
    if (React.isValidElement(trigger)) {
      return <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>;
    }

    return (
      <AlertDialogTrigger asChild>
        <Button {...buttonProps}>{trigger}</Button>
      </AlertDialogTrigger>
    );
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
      {renderTrigger()}
      <AlertDialogContentPrimitive>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description && (
            <AlertDialogDescription className={dialogDescriptionClassName}>{description}</AlertDialogDescription>
          )}
        </AlertDialogHeader>
        {children}
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => setIsOpen(false)}>{cancelText}</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              onConfirm?.();
              setIsOpen(false);
            }}
            disabled={isLoading}
            className={
              variant === 'destructive' ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : ''
            }
          >
            {confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContentPrimitive>
    </AlertDialog>
  );
};
